/**
 * The built-in desk (defs/desk.ts) as a data spec.
 *
 * How it works:
 *  - Top: the footprint rectangle, stretched with the object.
 *  - Inset line: a lighter inner rectangle so the desk reads as a worktop.
 *    Its inset is 6 % of the smaller side (`inset` formula), so its corners
 *    are formulas of the live size and the group uses `fixed` resize (the
 *    formulas already give the live coordinates).
 *  - Chair: the chair symbol (assets/chair.svg) at the Chair object's size,
 *    0.45 × 0.45 m, authored CENTRED ON THE ORIGIN with its backrest at −n.
 *    The `fixed` group keeps that size; its `offset` moves it in front of
 *    the desk (`chairCenter` past the +n edge) and turns it half a turn so
 *    it faces the desk. The `chair` toggle shows or hides it.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec } from 'shared-types';
import chairSvg from '../../assets/chair.svg?raw';
import { svgSymbolShapes } from './svgSymbol';

const W = 1.4;
const D = 0.7;
const HW = W / 2;
const HD = D / 2;
const INSET = Math.min(W, D) * 0.06;
/** Footprint of the chair symbol (the Chair object's default size). */
const CHAIR_SIZE = 0.45;

const chairShapes = svgSymbolShapes('chair', chairSvg, CHAIR_SIZE, CHAIR_SIZE, 'chair', 'Chair');

export const deskSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'FREE' },
  defaultSize: { width: W, depth: D, height: 0.75 },
  params: [{ key: 'chair', kind: 'boolean', label: 'chair', default: true }],
  formulas: [
    { key: 'inset', label: 'Inner line inset', expr: 'min(width, depth) * 0.06', note: 'Distance of the inner line from the edge: 6 % of the smaller side' },
    { key: 'chairGap', label: 'Chair gap', expr: '0.04', note: 'Gap between the desk edge and the front of the chair' },
    { key: 'chairCenter', label: 'Chair distance from center', expr: 'depth / 2 + chairGap + 0.45 / 2', note: 'Desk center to chair center (the chair symbol is 0.45 m deep)' },
  ],
  shapes: [
    { id: 'top', name: 'Top', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    {
      id: 'insetLine',
      name: 'Inner line',
      kind: 'rect',
      min: [-HW + INSET, -HD + INSET],
      max: [HW - INSET, HD - INSET],
      expr: { 'min.u': '-width / 2 + inset', 'min.n': '-depth / 2 + inset', 'max.u': 'width / 2 - inset', 'max.n': 'depth / 2 - inset' },
    },
    ...chairShapes,
  ],
  groups: [
    { id: 'top', name: 'Top', shapeIds: ['top'], resize: { u: 'stretch', n: 'stretch' } },
    { id: 'insetLine', name: 'Inner line', shapeIds: ['insetLine'], resize: { u: 'fixed', n: 'fixed' } },
    {
      id: 'chair',
      name: 'Chair',
      shapeIds: chairShapes.map((s) => s.id),
      resize: { u: 'fixed', n: 'fixed' },
      visibleWhen: { param: 'chair', equals: true },
      // In front of the desk (+n), turned half a turn so the backrest points away from it.
      offset: { n: 'chairCenter', rotate: 'pi' },
    },
  ],
  model3d: null,
};
